import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { useClickOutside } from 'qampo-ui-library';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import ROUTES, { locationPath } from '../../routes/routeConstants';
import { useAppSelector } from '../../store/hooks';
import styles from './UserMenu.module.css';

// Avatar menu holding the account-level actions (settings, logout) that used
// to sit at the bottom of the side drawer.
export const UserMenu = () => {
  const { t } = useTranslation();
  const keycloak = useAppSelector((state) => state.authenticationSlice.kcObject);
  const { activeLocationId } = useActiveLocation();
  const { name, email, initials } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);

  // Settings are location-scoped when a location is resolved, and fall back to
  // the account-level page otherwise.
  const settingsPath = activeLocationId
    ? locationPath(activeLocationId, ROUTES.settings)
    : `/${ROUTES.settings}`;

  const handleLogout = useCallback(() => {
    keycloak?.logout({ redirectUri: globalThis.location.origin });
  }, [keycloak]);

  useClickOutside(menuRef, () => {
    setIsOpen(false);
  });

  // Keyboard handling lives on the document while the menu is open: the wrapper
  // is a plain container, and this also works once focus has moved into a row.
  // Escape closes and hands focus back to the avatar; the arrows walk the rows.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        avatarRef.current?.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const rows = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>(`.${styles.menuItem}`) ?? []
      );
      if (rows.length === 0) return;
      event.preventDefault();
      const current = rows.indexOf(document.activeElement as HTMLElement);
      // Nothing inside the menu has focus yet: enter from whichever end the key
      // points at.
      if (current === -1) {
        (event.key === 'ArrowDown' ? rows[0] : rows[rows.length - 1])?.focus();
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      rows[(current + step + rows.length) % rows.length]?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={menuRef} className={styles.userMenu}>
      <button
        ref={avatarRef}
        type="button"
        className={styles.avatarButton}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => {
          // Opening with ArrowDown is the expected menu-button gesture; once
          // open, the document listener above takes over.
          if (event.key === 'ArrowDown' && !isOpen) setIsOpen(true);
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={name || t('menu')}
      >
        {initials}
      </button>
      {isOpen && (
        <div className={styles.popover}>
          <div className={styles.identity}>
            <span className={styles.userName}>{name}</span>
            {email && <span className={styles.userEmail}>{email}</span>}
          </div>
          {/* The menu role covers only the actionable rows: a menu may not own
              arbitrary content, and the identity block is a heading. */}
          <div role="menu" aria-label={t('menu')} className={styles.menuRows}>
            <NavLink
              to={settingsPath}
              className={({ isActive }) =>
                `${styles.menuItem} ${isActive ? styles.menuItemActive : ''}`
              }
              role="menuitem"
              onClick={() => setIsOpen(false)}
            >
              {t('nav-settings')}
            </NavLink>
            <button type="button" className={styles.menuItem} role="menuitem" onClick={handleLogout}>
              {t('logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
